import { XClose } from "@untitledui/icons";
import { useState } from "react";
import { Dialog as AriaDialog, Modal as AriaModal, ModalOverlay as AriaModalOverlay } from "react-aria-components";
import { useAddMerchantsToWorkspace } from "@/api/admin/workspace-mutations";
import { Button } from "@/components/base/buttons/button";
import { error } from "@/providers/ToastProvider";
import { WorkspacesPicker } from "../components/workspaces-picker";

type Props = {
  merchantId: string;
  merchantName: string;
  /** IDs of workspaces the merchant is already enrolled in — pre-checked */
  enrolledWorkspaceIds: string[];
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onDone?: () => void;
};

export const SelectWorkspacesForMerchantModal = ({
  merchantId,
  merchantName,
  enrolledWorkspaceIds,
  open,
  onOpenChange,
  onDone,
}: Props) => {
  const [selectedIds, setSelectedIds] = useState<string[]>(enrolledWorkspaceIds);
  const addMerchants = useAddMerchantsToWorkspace();

  const handleOpen = (isOpen: boolean) => {
    if (isOpen) setSelectedIds(enrolledWorkspaceIds);
    onOpenChange(isOpen);
  };

  const handleAdd = async () => {
    const enrolledSet = new Set(enrolledWorkspaceIds);
    const newWorkspaceIds = selectedIds.filter((id) => !enrolledSet.has(id));

    try {
      for (const workspaceId of newWorkspaceIds) {
        await addMerchants.mutateAsync({ workspaceId, merchantIds: [merchantId] });
      }
    } catch {
      error("Failed to add merchant to workspace");
      return;
    }
    onOpenChange(false);
    onDone?.();
  };

  const handleCancel = () => {
    setSelectedIds(enrolledWorkspaceIds);
    onOpenChange(false);
    onDone?.();
  };

  return (
    <AriaModalOverlay
      isOpen={open}
      onOpenChange={handleOpen}
      isDismissable={false}
      className="fixed inset-0 z-[120] bg-black/70"
    >
      <AriaModal className="dark-mode absolute inset-x-8 inset-y-8 outline-none bg-primary rounded-2xl overflow-hidden flex flex-col">
        <AriaDialog className="w-full h-full flex flex-col outline-none py-8">
          <div className="shrink-0 flex justify-end px-8 pb-2">
            <button
              type="button"
              onClick={handleCancel}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-secondary border border-primary rounded-lg hover:bg-white/10 transition-colors"
            >
              <XClose className="size-4" />
              Cancel
            </button>
          </div>

          <div className="flex-1 flex flex-col items-center px-8 overflow-hidden min-h-0">
            <div className="w-full max-w-[560px] flex flex-col flex-1 overflow-hidden gap-3">
              <WorkspacesPicker
                merchantName={merchantName}
                value={selectedIds}
                onChange={setSelectedIds}
                onNavigateAway={handleCancel}
              />
            </div>
          </div>

          <div className="shrink-0 flex justify-center px-8 py-6">
            <div className="w-full max-w-[560px]">
              <Button
                size="lg"
                color="primary"
                className="w-full"
                onClick={handleAdd}
                isDisabled={addMerchants.isPending}
              >
                {addMerchants.isPending ? "Adding…" : `Add ${merchantName} to Workspace`}
              </Button>
            </div>
          </div>
        </AriaDialog>
      </AriaModal>
    </AriaModalOverlay>
  );
};
